import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useField } from "formik";
import { useCallback, useId, useRef } from "react";
import { FiImage, FiMaximize2, FiTrash2, FiUploadCloud } from "react-icons/fi";
import { APIURL } from "../../utils/apiCall";

type Props = {
  name?: string;
};

function readAndResizeImage(file: File): Promise<string> {
  const maxWidth = 1200;
  const maxHeight = 1200;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Não foi possível ler o arquivo."));
    reader.onloadend = () => {
      const imagePreview = reader.result as string;
      const wrap = document.createElement("div");
      const resizeArea = document.createElement("canvas");
      wrap.appendChild(resizeArea);
      wrap.style.cssText =
        "position:absolute;left:-9999px;top:-9999px;width:0;height:0;overflow:hidden;";
      document.body.appendChild(wrap);

      const oImg = document.createElement("img");
      oImg.src = imagePreview;
      oImg.onload = () => {
        let height = oImg.height;
        let width = oImg.width;
        const capW = maxWidth * 1.2;
        const capH = maxHeight * 1.2;
        if (width > capW) {
          height = Math.round((height * capW) / width);
          width = capW;
        }
        if (height > capH) {
          width = Math.round((width * capH) / height);
          height = capH;
        }
        resizeArea.width = width;
        resizeArea.height = height;
        const ctx = resizeArea.getContext("2d");
        ctx?.drawImage(oImg, 0, 0, width, height);
        const dataUrl = resizeArea.toDataURL("image/jpeg", 0.88);
        document.body.removeChild(wrap);
        resolve(dataUrl);
      };
      oImg.onerror = () => {
        document.body.removeChild(wrap);
        reject(new Error("Imagem inválida."));
      };
    };
    reader.readAsDataURL(file);
  });
}

export function ComprovanteFotoField({ name = "foto" }: Props) {
  const inputId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const [, { error, touched, value }, { setValue }] = useField<string>(name);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const previewUrl =
    value && !String(value).startsWith("data:")
      ? `${APIURL.replace("/api", "/storage/images/")}${value}`
      : value || "";

  const hasImage = Boolean(value);

  const onFile = useCallback(
    async (fileList: FileList | null) => {
      const file = fileList?.[0];
      if (!file || !file.type.startsWith("image/")) {
        return;
      }
      try {
        const resized = await readAndResizeImage(file);
        setValue(resized);
      } catch {
        /* silencioso: extensão inválida etc. */
      }
    },
    [setValue]
  );

  return (
    <FormControl
      px={2}
      mb={3}
      width="full"
      isInvalid={Boolean(touched && error)}
    >
      <FormLabel fontWeight="semibold" color="gray.700" mb={2}>
        Comprovante em foto
      </FormLabel>
      <Box
        role="group"
        borderWidth="1px"
        borderStyle="dashed"
        borderColor={touched && error ? "red.300" : "gray.300"}
        borderRadius="xl"
        bg={hasImage ? "white" : "gray.50"}
        transition="border-color 0.2s, box-shadow 0.2s, background 0.2s"
        _hover={{
          borderColor: "teal.400",
          bg: hasImage ? "white" : "teal.50",
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void onFile(e.dataTransfer.files);
        }}
      >
        <Flex
          direction={{ base: "column", sm: "row" }}
          align={{ base: "stretch", sm: "center" }}
          gap={4}
          p={4}
        >
          <Box
            flexShrink={0}
            alignSelf={{ base: "center", sm: "flex-start" }}
            w={{ base: "100%", sm: "140px" }}
            maxW={{ base: "220px", sm: "140px" }}
            position="relative"
          >
            {hasImage ? (
              <Box
                borderRadius="lg"
                overflow="hidden"
                boxShadow="md"
                borderWidth="1px"
                borderColor="gray.200"
                aspectRatio={1}
              >
                <Image
                  src={previewUrl}
                  alt="Pré-visualização do comprovante"
                  objectFit="cover"
                  w="full"
                  h="full"
                  fallback={
                    <Flex
                      align="center"
                      justify="center"
                      bg="gray.100"
                      aspectRatio={1}
                    >
                      <Icon as={FiImage} boxSize={10} color="gray.400" />
                    </Flex>
                  }
                />
              </Box>
            ) : (
              <Flex
                direction="column"
                align="center"
                justify="center"
                aspectRatio={1}
                borderRadius="lg"
                bg="white"
                borderWidth="1px"
                borderColor="gray.200"
                color="gray.500"
              >
                <Icon as={FiImage} boxSize={10} mb={2} />
                <Text fontSize="xs" textAlign="center" px={2}>
                  Nenhuma foto
                </Text>
              </Flex>
            )}
          </Box>

          <Flex direction="column" flex={1} gap={3} minW={0}>
            <Flex wrap="wrap" gap={2}>
              <Button
                size="sm"
                colorScheme="teal"
                leftIcon={<FiUploadCloud />}
                onClick={() => fileRef.current?.click()}
              >
                {hasImage ? "Trocar foto" : "Escolher arquivo"}
              </Button>
              {hasImage && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    leftIcon={<FiMaximize2 />}
                    onClick={onOpen}
                  >
                    Ampliar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    leftIcon={<FiTrash2 />}
                    onClick={() => setValue("")}
                  >
                    Remover
                  </Button>
                </>
              )}
            </Flex>
            <Text fontSize="xs" color="gray.500">
              Formatos: JPG, PNG, WEBP. Você também pode arrastar e soltar aqui.
            </Text>
          </Flex>
        </Flex>
      </Box>

      <input
        ref={fileRef}
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          void onFile(e.target.files);
          e.target.value = "";
        }}
      />

      {error && <FormErrorMessage>{error}</FormErrorMessage>}

      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent mx={3}>
          <ModalHeader>Comprovante</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={2}>
            {hasImage && (
              <Image
                src={previewUrl}
                alt="Comprovante em tamanho maior"
                maxH="70vh"
                w="full"
                objectFit="contain"
                borderRadius="md"
              />
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="teal" onClick={onClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </FormControl>
  );
}
